import {
    managedPaymentProviders,
    unmanagedPaymentProviders,
} from "@@/shared/payments/payment-providers";
import { priceZodSchema } from "@@/shared/prices/price";
import { asArray } from "@@/utilities/as-array";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import type {
    _OrderEventBaseFactoryParams} from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderEventIdZodSchema } from "../_order-event-id";

const orderEventPaymentPendingZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("PENDING_PAYMENT_ONPREMISE"),
        paymentProvider: z.enum([
            ...unmanagedPaymentProviders,
            ...managedPaymentProviders.filter(p => p !== "PAY_REQUEST"),
        ]),
        /**
         * NOTE! Old events won't have price, but new ones will,
         * old ones where only connected to the creation of an order
         * new ones may be connected to other events and need to have
         * a price, all new ones will have one, or else it should be the price
         * of the original order that's supposed to be paid later
         */
        price: priceZodSchema.optional(),
        paymentFor: z.object({
            orderEventId: orderEventIdZodSchema.or(
                z.array(orderEventIdZodSchema),
            ),
            /**
             * If there is no payment id, the payment is to old
             */
            paymentId: z.string().optional(),
        }),
    }),
);

type OrderPaymentPendingEvent = z.infer<
    typeof orderEventPaymentPendingZodObject
>;
const orderEventPaymentPendingZodSchema =
    orderEventPaymentPendingZodObject as z.ZodSchema<OrderPaymentPendingEvent>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPaymentPendingEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;
type Params = Except<FactoryParams, "signature">;

function orderEventPendingPaymentOnPremiseFactory(
    params: Params,
): OrderPaymentPendingEvent {
    return {
        ..._orderEventBaseFactory({
            ...params,
            signature: signatureFactory(
                params.orderId,
                "PENDING_PAYMENT_ONPREMISE",
                asArray(params.paymentFor.orderEventId),
            ),
        }),
        paymentFor: params.paymentFor,
        type: "PENDING_PAYMENT_ONPREMISE",
        paymentProvider: params.paymentProvider,
        price: params.price,
    };
}

const isOrderPaymentPendingEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPaymentPendingEvent => {
    return (
        event._type === "ORDER_EVENT" &&
        event?.type === "PENDING_PAYMENT_ONPREMISE"
    );
};

export {
    isOrderPaymentPendingEvent,
    orderEventPaymentPendingZodObject,
    orderEventPaymentPendingZodSchema,
    orderEventPendingPaymentOnPremiseFactory,
};
export type { OrderPaymentPendingEvent };
